import React, { useEffect, useRef, useState } from 'react';
import { Bell } from 'lucide-react';
import { supabase } from '../lib/supabase';
import { useChat } from '../context/ChatContext';

interface NotificationsDropdownProps {
  userId: string;
}

export default function NotificationsDropdown({ userId }: NotificationsDropdownProps) {
  const [isOpen, setIsOpen] = useState(false);
  const { notifications, unreadNotificationsCount, loading } = useChat();
  const [userRole, setUserRole] = useState<string | null>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  // Still fetch role locally as it controls visibility logic specific to this component? 
  // Or should we move role check to ChatContext? 
  // For now, keep role check here for minimal impact, but reuse notifications from context.

  useEffect(() => {
    async function fetchUserRole() {
      // We can optimize this by caching or using a ProfileContext if available, 
      // but for now let's keep it simple.
      // Actually, check if we can get it from ChatContext user? No, ChatContext doesn't expose role yet.
      const { data } = await supabase
        .from('profiles')
        .select('role')
        .eq('id', userId)
        .maybeSingle();
      setUserRole(data?.role || null);
    }
    fetchUserRole();
  }, [userId]);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const toggleOpen = () => {
    setIsOpen(!isOpen);
    // TODO: Mark as read
  };

  if (loading || userRole !== 'editor') return null;

  return (
    <div className="relative" ref={menuRef}>
      <button
        onClick={toggleOpen}
        className="relative text-gray-700 hover:text-gray-900 dark:text-gray-300 dark:hover:text-white transition-colors duration-300"
      >
        <Bell className="h-5 w-5" />
        {unreadNotificationsCount > 0 && (
          <span className="absolute top-0 right-0 inline-flex items-center justify-center px-2 py-1 text-xs font-bold leading-none text-red-100 transform translate-x-1/2 -translate-y-1/2 bg-red-600 rounded-full">
            {unreadNotificationsCount}
          </span>
        )}
      </button>
      {isOpen && (
        <div className="absolute right-0 mt-2 w-80 rounded-md shadow-lg bg-white dark:bg-gray-800 ring-1 ring-gray-200 dark:ring-black dark:ring-opacity-5 max-h-96 overflow-y-auto z-50">
          <div className="py-2">
            {notifications.length === 0 ? (
              <div className="px-4 py-2 text-sm text-gray-500 dark:text-gray-400">No notifications</div>
            ) : (
              notifications.map(n => (
                <div key={n.id} className="px-4 py-2 border-b border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700">
                  <div className="text-sm text-gray-700 dark:text-gray-300">{n.message}</div>
                  <div className="text-xs text-gray-500">{new Date(n.created_at).toLocaleString()}</div>
                </div>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}
